import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Wrench } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { AtelierItem } from "@/components/atelier/AtelierItem";
import { AtelierKeys } from "@/components/atelier/AtelierKeys";
import { AtelierAudit } from "@/components/atelier/AtelierAudit";
import { StatusPill } from "@/components/atelier/StatusPill";
import { AtelierLink } from "@/components/atelier/AtelierLink";
import { PageHero } from "@/components/layout/PageHero";

// Admin → Atelier (creator only): versions sent to the atelier, E3 reviewer key, audit log.
export default function AdminAtelier() {
  const { t, fmtDate } = useI18n();
  const [params, setParams] = useSearchParams();
  const [scope, setScope] = useState("open");
  const [list, setList] = useState(null);
  const [tick, setTick] = useState(0);
  const sel = params.get("item");
  const reload = useCallback(() => setTick((n) => n + 1), []);
  useEffect(() => { api.get("/admin/atelier/queue", { params: { scope } }).then(({ data }) => setList(data)).catch(() => setList([])); }, [scope, tick]);
  return (
    <div data-testid="admin-atelier-page">
      <PageHero eyebrow={t("admin.eyebrow")} title={t("atelier.title")} testId="atelier-hero">
        <p className="mt-4 flex max-w-3xl items-start gap-2 break-words text-sm text-neutral-400"><Wrench className="mt-0.5 h-4 w-4 shrink-0 text-[var(--gold)]" /> {t("atelier.intro")}</p>
      </PageHero>
      <div className="mx-auto grid min-w-0 max-w-7xl gap-6 px-4 pb-16 sm:px-8">
      <AtelierLink onDone={reload} />
      <section className="card-carbon min-w-0 space-y-3 rounded-2xl p-4" data-testid="atelier-queue">
        <div className="flex flex-wrap gap-2">
          {["open", "all"].map((s) => (
            <button key={s} type="button" aria-pressed={scope === s} onClick={() => setScope(s)} data-testid={`atelier-scope-${s}`}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${scope === s ? "border-[var(--gold)] bg-[var(--gold)] text-black" : "border-neutral-700 text-neutral-200"}`}>{t(`atelier.scope.${s}`)}</button>
          ))}
        </div>
        {list && !list.length && <p className="text-sm text-neutral-500" data-testid="atelier-empty">{t("atelier.empty")}</p>}
        <ul className="min-w-0 divide-y divide-neutral-800">
          {(list || []).map((it) => (
            <li key={it.id}>
              <button type="button" onClick={() => setParams({ item: it.id })} data-testid={`atelier-row-${it.id}`}
                className={`flex w-full min-w-0 flex-wrap items-center gap-x-3 gap-y-1 py-2 text-start text-sm ${sel === it.id ? "text-[var(--gold-bright)]" : "text-neutral-200"}`}>
                <span className="min-w-0 break-words font-semibold">{it.app_name}</span>
                <span className="text-xs text-neutral-400" dir="ltr">v{it.version}{it.version_code ? ` (build ${it.version_code})` : ""}</span>
                <span className="text-xs text-neutral-500">{fmtDate(it.uploaded_at)}</span>
                <StatusPill status={it.status} testId={`atelier-row-${it.id}-status`} />
                {!it.gate_ok && <span className="text-xs font-semibold text-red-300">{t("atelier.blocking")}</span>}
              </button>
            </li>
          ))}
        </ul>
      </section>
      {sel && <AtelierItem id={sel} onChanged={reload} />}
      <AtelierKeys />
      <AtelierAudit tick={tick} />
      </div>
    </div>
  );
}
